import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type { BarcodeSettingsDto, BarcodeSettingsInput, BranchRecordDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { useToast } from "../../components/Toast";

// Settings › Branches › Barcode settings, ported from the prototype (erp-settings.js "/branch-barcode/:storeId"):
// general label fields, scale (weight) label fields and a live preview.

type K = keyof BarcodeSettingsInput;
type Draft = Record<K, string | number | boolean>;
const NUMS: K[] = ["size", "height", "width", "marginTop", "marginBottom", "marginRight", "marginLeft", "weightLeftFlag", "weightCodeLength", "weightQtyLength", "weightCheckLength"];
const SHOWS: K[] = ["showTitle", "showText", "showName", "showPrice"];
const ALL: K[] = ["titleAr", "titleEn", ...NUMS, ...SHOWS, "weightActive"];
const LEN: K[] = ["weightLeftFlag", "weightCodeLength", "weightQtyLength", "weightCheckLength"];
const LEN_LABEL: Record<string, string> = { weightLeftFlag: "weightLeftFlag", weightCodeLength: "weightCodeLen", weightQtyLength: "weightQtyLen", weightCheckLength: "weightCheckLen" };
const pick = (d: BarcodeSettingsDto): Draft => Object.fromEntries(ALL.map((k) => [k, d[k]])) as Draft;

// A sample label only: Delta has no items yet, so the preview uses the prototype's sample barcode.
const SAMPLE_CODE = "6281000000017";

function Preview({ v }: { v: Draft }) {
  const { t, lang } = useLang();
  const mm = (n: unknown) => Math.max(0, Number(n) || 0) * 3.2;
  const title = lang === "ar" ? v.titleAr || v.titleEn : v.titleEn || v.titleAr;
  // The left flag is a value (for example 21); its digit count is part of the 13.
  const digits = LEN.map((k, i) => (i === 0 ? String(v[k] ?? "").length : Math.max(0, Number(v[k]) || 0)));
  const total = digits.reduce((a, b) => a + b, 0);
  const fit = (x: string, n: number) => (n > 0 ? x.padStart(n, "0").slice(-n) : "");
  const sample = [fit(String(v.weightLeftFlag ?? ""), digits[0]!), fit("12345", digits[1]!), fit("01250", digits[2]!), fit("7", digits[3]!)];
  return (
    <>
      <div className="bclbl-stage"><div className="bclbl" data-testid="label-preview" style={{ width: mm(v.width), minHeight: mm(v.height), padding: `${mm(v.marginTop)}px ${mm(v.marginRight)}px ${mm(v.marginBottom)}px ${mm(v.marginLeft)}px`, fontSize: Math.max(8, Number(v.size) || 10) }}>
        {v.showTitle && <div className="bclbl-t">{String(title ?? "")}</div>}
        {v.showName && <div className="bclbl-n">{t("sampleItem")}</div>}
        <div className="bclbl-bars" dir="ltr">{SAMPLE_CODE.split("").map((c, i) => <i key={i} style={{ width: 1 + (+c % 3), marginInlineEnd: 1 + ((+c + i) % 2) }} />)}</div>
        {v.showText && <div className="bclbl-c num" dir="ltr">{SAMPLE_CODE}</div>}
        {v.showPrice && <div className="bclbl-p num">{t("samplePrice")}</div>}
      </div></div>
      <div className="hint pad">{t("labelPreviewHint")}</div>
      {v.weightActive && (
        <div className="wgt"><div className="wgt-h">{t("weightSample")}</div>
          <div className="wgt-code num" dir="ltr">{sample.map((x, i) => <span key={i} className={`wg-${i}`} title={t(LEN_LABEL[LEN[i]!]!)}>{x}</span>)}</div>
          <div className="wgt-legend">{LEN.map((k, i) => <span key={k}><i className={`wg-${i}`} />{t(LEN_LABEL[k]!)}</span>)}</div>
          {total !== 13 ? <div className="hint warn">{t("weightNot13", { n: total })}</div> : <div className="hint">{t("weight13ok")}</div>}
        </div>
      )}
    </>
  );
}

export function BarcodeSettings() {
  const { storeId = "" } = useParams();
  const { t, L } = useLang();
  const toast = useToast();
  const [branch, setBranch] = useState<BranchRecordDto | null>(null);
  const [saved, setSaved] = useState<Draft | null>(null);
  const [v, setV] = useState<Draft | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const [b, d] = await Promise.all([api<BranchRecordDto>("GET", `/settings/branches/${storeId}`), api<BarcodeSettingsDto>("GET", `/settings/branches/${storeId}/barcode`)]);
      setBranch(b); setSaved(pick(d)); setV(pick(d)); setErrs({});
    } catch (e) { setFailed(errorText(t, e)); }
  }, [storeId, t]);
  useEffect(() => { void load(); }, [load]);
  const dirty = useMemo(() => !!v && !!saved && ALL.some((k) => v[k] !== saved[k]), [v, saved]);
  const set = (k: K, x: string | number | boolean) => { setV((o) => ({ ...o!, [k]: x })); setErrs((e) => ({ ...e, [k]: "" })); };

  async function save() {
    if (!v) return;
    const e: Record<string, string> = {};
    (["size", "height", "width"] as K[]).forEach((k) => { if (!(Number(v[k]) > 0)) e[k] = t("positiveNumber"); });
    (["marginTop", "marginBottom", "marginRight", "marginLeft"] as K[]).forEach((k) => { if (v[k] === "" || Number(v[k]) < 0) e[k] = t("zeroOrMore"); });
    if (v.weightActive) LEN.forEach((k) => { if (v[k] === "" || !(Number.isInteger(Number(v[k])) && Number(v[k]) >= 0)) e[k] = t("wholeNumber"); });
    setErrs(e);
    if (Object.keys(e).length) { document.querySelector<HTMLInputElement>(`#pf-${Object.keys(e)[0]}`)?.focus(); return; }
    setBusy(true);
    try {
      const body = Object.fromEntries(ALL.map((k) => [k, NUMS.includes(k) ? Number(v[k]) : v[k]])) as unknown as BarcodeSettingsInput;
      const d = await api<BarcodeSettingsDto>("PUT", `/settings/branches/${storeId}/barcode`, body);
      setSaved(pick(d)); setV(pick(d));
      toast(t("barcodeSavedOk", { x: L(branch) }));
    } catch (err) { setErrs(fieldErrorsOf(t, err)); toast(errorText(t, err)); }
    finally { setBusy(false); }
  }

  const num = (k: K, label: string) => (
    <div className={`field ${errs[k] ? "invalid" : ""}`}>
      <label htmlFor={`pf-${k}`}>{label}</label>
      <input id={`pf-${k}`} className="control num" style={{ textAlign: "start" }} type="number" min={0} step="any" value={String(v![k])} onChange={(e) => set(k, e.target.value === "" ? "" : Number(e.target.value))} />
      <div className="ferr" role="alert">{errs[k]}</div>
    </div>
  );
  const txt = (k: K, label: string) => (
    <div className="field"><label htmlFor={`pf-${k}`}>{label}</label><input id={`pf-${k}`} className="control" value={String(v![k])} onChange={(e) => set(k, e.target.value)} autoComplete="off" /><div className="ferr" role="alert">{errs[k]}</div></div>
  );
  const check = (k: K, label: string) => (
    <label key={k} className="check-row"><input type="checkbox" id={`pf-${k}`} checked={!!v![k]} onChange={(e) => set(k, e.target.checked)} /><span>{label}</span></label>
  );

  const body = failed ? (
    <section className="card"><div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p></div></section>
  ) : !v ? <section className="card"><div className="state-box"><p>{t("loading")}</p></div></section> : (
    <div className="detail-grid wide-side">
      <div className="set-stack">
        <section className="card"><div className="card-head"><h2>{t("barcodeGeneral")}</h2></div><div className="card-body"><div className="form-grid">
          {txt("titleAr", t("nameAr"))}{txt("titleEn", t("nameEn"))}{num("size", t("sizeF"))}{num("height", t("heightF"))}{num("width", t("widthF"))}<div />
          {num("marginTop", t("marginTop"))}{num("marginBottom", t("marginBottom"))}{num("marginRight", t("marginRight"))}{num("marginLeft", t("marginLeft"))}
          <div className="field full"><span className="label">{t("labelShows")}</span><div className="check-line">{SHOWS.map((k) => check(k, t(k)))}</div></div>
        </div></div></section>
        <section className="card"><div className="card-head"><h2>{t("barcodeWeight")}</h2></div><div className="card-body"><div className="form-grid">
          <div className="field full">{check("weightActive", t("weightActive"))}</div>
          {v.weightActive ? LEN.map((k) => <span key={k} style={{ display: "contents" }}>{num(k, t(LEN_LABEL[k]!))}</span>) : <p className="hint full">{t("weightOffHint")}</p>}
        </div></div></section>
      </div>
      <aside className="detail-side"><section className="card"><div className="card-head"><h2>{t("labelPreview")}</h2></div><div className="card-body"><Preview v={v} /></div></section></aside>
    </div>
  );

  const bar = v && (
    <div className="action-bar">
      <div className="ab-stats"><div><div className="k">{t("status")}</div><div className="v" data-testid="form-state">{dirty ? <span className="pill warn">{t("unsavedChanges")}</span> : <span className="pill good">{t("allSaved")}</span>}</div></div></div>
      <div className="ab-actions">
        <button className="btn" disabled={!dirty || busy} onClick={() => { setV(saved); setErrs({}); }}>{t("discardChanges")}</button>
        <button className="btn btn-primary" disabled={!dirty || busy} onClick={() => void save()}><Icon name="check" />{busy ? t("saving") : t("save")}</button>
      </div>
    </div>
  );

  return (
    <Shell navKey="/settings/branches" bar={bar}>
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("setBranches"), "/settings/branches"], [L(branch) || "…"], [t("barcodeSettings")]]} title={t("barcodeSettings")}
        badge={branch && <span className="pill">{L(branch)}</span>} />
      {body}
    </Shell>
  );
}
